import type { ResourceKey } from 'i18next';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import { Suspense } from 'react';
import { Outlet, Navigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdCard,
  faFileLines,
  faUserGroup,
  faShieldCheck,
} from '@fortawesome/pro-regular-svg-icons';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useAdminPermissions } from 'src/hooks/admin';

import { useTranslate } from 'src/locales';
import { DashboardContent } from 'src/layouts/dashboard';
import { useGetManagedAdminRoles } from 'src/actions/admin/role';
import { useGetUserDetail } from 'src/actions/admin/user/user-detail';

import { LoadingScreen } from 'src/components/loading-screen';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

import { AdminUserDetailContext } from './admin-user-detail-context';

export type AdminUserDetailTab = 'general' | 'roles' | 'groups' | 'license';

const TAB_CONFIG: {
  value: AdminUserDetailTab;
  labelKey: ResourceKey;
  icon: IconDefinition;
  path: string;
}[] = [
  { value: 'general', labelKey: 'userDetail.tabs.general', icon: faIdCard, path: '' },
  {
    value: 'groups',
    labelKey: 'userDetail.tabs.groups',
    icon: faUserGroup,
    path: '/groups',
  },
  {
    value: 'license',
    labelKey: 'userDetail.tabs.license',
    icon: faFileLines,
    path: '/license',
  },
  {
    value: 'roles',
    labelKey: 'userDetail.tabs.adminRoles',
    icon: faShieldCheck,
    path: '/roles',
  },
];

export function AdminUserDetailLayout() {
  const { id } = useParams<{ id: string }>();
  const pathname = usePathname();
  const { t } = useTranslate(['navbar', 'administration', 'common']);

  const userId = Number(id);

  const { mainGroupId, canDisableYearlyCreditRenewalForUser } = useAdminPermissions();

  const { user, userLoading, userRefresh } = useGetUserDetail({ mainGroupId, userId });
  const { managedAdminRolesEmpty } = useGetManagedAdminRoles();

  const visibleTabs = managedAdminRolesEmpty
    ? TAB_CONFIG.filter((tab) => tab.value !== 'roles')
    : TAB_CONFIG;

  const activeTab: AdminUserDetailTab =
    TAB_CONFIG.find((tab) => tab.path && pathname.endsWith(tab.path))?.value || 'general';

  if (userLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/dashboard" replace />;
  }

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username;

  return (
    <AdminUserDetailContext.Provider
      value={{
        user,
        mainGroupId,
        refresh: userRefresh,
        canEditYearlyCreditRenewal: canDisableYearlyCreditRenewalForUser,
      }}
    >
      <DashboardContent maxWidth="xl">
        <CustomBreadcrumbs
          heading={fullName}
          links={[
            {
              name: t('administration.title', { ns: 'navbar' }),
              href: paths.dashboard.root,
            },
            {
              name: t('userDetail.breadcrumb', { ns: 'administration' }),
              href: paths.admin.userManagement,
            },
            { name: fullName },
          ]}
          sx={{ mb: 3 }}
        />

        <Tabs value={activeTab} sx={{ mb: 3 }}>
          {visibleTabs.map((tab) => (
            <Tab
              component={RouterLink}
              key={tab.value}
              value={tab.value}
              href={paths.admin.user[tab.value](userId)}
              label={t(tab.labelKey, { ns: 'administration' })}
              icon={<FontAwesomeIcon icon={tab.icon} size="lg" />}
            />
          ))}
        </Tabs>

        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </DashboardContent>
    </AdminUserDetailContext.Provider>
  );
}
